import * as React from "react";
import { useCallback, useState } from "react";
import { useRecoilValue, useSetRecoilState, atom, RecoilState, RecoilValue } from "recoil";
import { useHasFileVisibilityPermission, useIsCertificateEditMode } from "lib/userInfo";
import { TenantRole } from "graphqlBase/types";
import translations from "translations";

interface UseMatrixParameterVisibilityProps {
  getFieldValue: (fieldName: string) => RecoilValue<unknown> | null;
}

interface UseMatrixParameterVisibilityReturn {
  showVisibilityControls: boolean;
  openVisibilityModal: boolean;
  setOpenVisibilityModal: (open: boolean) => void;
  hasVisibilityGrants: () => boolean;
  getVisibilityTooltip: () => string;
  handleVisibilitySave: (roles: TenantRole[], tenantIds: string[]) => void;
  currentRoles: TenantRole[] | undefined;
  currentTenantIds: string[] | undefined;
}

export const useMatrixParameterVisibility = ({
  getFieldValue,
}: UseMatrixParameterVisibilityProps): UseMatrixParameterVisibilityReturn => {
  const dummyRolesAtom = React.useMemo(
    () =>
      atom<TenantRole[] | undefined>({
        key: `matrixVisibility-roles-dummy-${Math.random()}`,
        default: undefined,
      }),
    []
  );

  const dummyTenantIdsAtom = React.useMemo(
    () =>
      atom<string[] | undefined>({
        key: `matrixVisibility-tenantIds-dummy-${Math.random()}`,
        default: undefined,
      }),
    []
  );

  const hasFileVisibilityPermission = useHasFileVisibilityPermission();
  const isCertificateEditMode = useIsCertificateEditMode();
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);

  const grantAccessToMatrixParametersRolesSelector = React.useMemo(() => {
    try {
      return getFieldValue("grantAccessToMatrixParametersRoles");
    } catch {
      return null;
    }
  }, [getFieldValue]);

  const grantAccessToMatrixParametersTenantIdsSelector = React.useMemo(() => {
    try {
      return getFieldValue("grantAccessToMatrixParametersTenantIds");
    } catch {
      return null;
    }
  }, [getFieldValue]);

  const currentRoles = useRecoilValue(grantAccessToMatrixParametersRolesSelector || dummyRolesAtom) as
    | TenantRole[]
    | undefined;
  const currentTenantIds = useRecoilValue(grantAccessToMatrixParametersTenantIdsSelector || dummyTenantIdsAtom) as
    | string[]
    | undefined;

  const setRoles = useSetRecoilState(
    (grantAccessToMatrixParametersRolesSelector || dummyRolesAtom) as RecoilState<TenantRole[] | undefined>
  );
  const setTenantIds = useSetRecoilState(
    (grantAccessToMatrixParametersTenantIdsSelector || dummyTenantIdsAtom) as RecoilState<string[] | undefined>
  );

  const showVisibilityControls = hasFileVisibilityPermission && !isCertificateEditMode;

  const hasVisibilityGrants = useCallback(() => {
    return (currentRoles && currentRoles.length > 0) || (currentTenantIds && currentTenantIds.length > 0) || false;
  }, [currentRoles, currentTenantIds]);

  const getVisibilityTooltip = useCallback(() => {
    if (!currentRoles && !currentTenantIds) {
      return translations.pages.certificateUpload.matrixVisibility.configureAccess;
    }

    const hasGrants = (currentRoles && currentRoles.length > 0) || (currentTenantIds && currentTenantIds.length > 0);
    if (!hasGrants) {
      return translations.pages.certificateUpload.matrixVisibility.configureAccess;
    }

    const grantSummary: string[] = [];
    if (currentRoles && currentRoles.length > 0) {
      grantSummary.push(
        `${translations.pages.certificateUpload.matrixVisibility.grantAccessToRoles}: ${currentRoles.join(", ")}`
      );
    }
    if (currentTenantIds && currentTenantIds.length > 0) {
      grantSummary.push(
        `${translations.pages.certificateUpload.matrixVisibility.grantAccessToTenants}: ${currentTenantIds.length} tenant(s)`
      );
    }
    return grantSummary.join(" | ");
  }, [currentRoles, currentTenantIds]);

  const handleVisibilitySave = useCallback(
    (roles: TenantRole[], tenantIds: string[]) => {
      setRoles(roles);
      setTenantIds(tenantIds);
      setOpenVisibilityModal(false);
    },
    [setRoles, setTenantIds]
  );

  return {
    showVisibilityControls,
    openVisibilityModal,
    setOpenVisibilityModal,
    hasVisibilityGrants,
    getVisibilityTooltip,
    handleVisibilitySave,
    currentRoles,
    currentTenantIds,
  };
};
